import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Inbox, PencilLine, Send, Clock } from "lucide-react";
import { useCoachFeedbackWorkbench } from "@/lib/coachWorkbenchQueries";

const TABS = [
  { key: "requests", label: "Requests", icon: Inbox, empty: "No open Feedback requests." },
  { key: "drafts", label: "Drafts", icon: PencilLine, empty: "No drafts in progress." },
  { key: "published", label: "Published", icon: Send, empty: "Nothing published yet." },
  { key: "waiting", label: "Waiting", icon: Clock, empty: "Nothing waiting on the client to read." },
];

const SCOPE_LABEL = { workspace_shared: "Team", member_private: "Private" };

/**
 * Master v5 Prompt 19 — cross-Workspace Feedback workbench. Requests/
 * Drafts/Published queues are exactly what those words mean; "Waiting"
 * is published Feedback the owning member hasn't read yet — the one
 * queue state the other three don't already cover (see
 * coach_workbench_routes.py's docstring for why).
 *
 * This is a triage/discovery surface, not a second compose UI: clicking
 * any row deep-links into the EXISTING per-Workspace Feedback
 * request/detail page (FeedbackRequestDetail.jsx/FeedbackDetail.jsx),
 * which already owns the full compose/evidence/AudienceGate/publish
 * flow — nothing about that flow is reimplemented here.
 */
export default function CoachFeedbackWorkbenchPage() {
  const { data, isPending, isError, error } = useCoachFeedbackWorkbench();
  const [tab, setTab] = useState("requests");
  const navigate = useNavigate();

  function openRow(row) {
    if (row.kind === "request") {
      navigate(`/rehearsal-room/w/${row.workspace_id}/feedback/request/${row.item.frq_id}`);
    } else {
      navigate(`/rehearsal-room/w/${row.workspace_id}/feedback/${row.item.fbk_id}`);
    }
  }

  const rows = data?.[tab] || [];

  return (
    <div data-testid="coach-feedback-workbench-page">
      <h1 className="font-serif text-2xl text-ink mb-1">Feedback</h1>
      <p className="text-sm text-ink-soft mb-5">Every open request, in-progress draft and published Feedback across your Workspaces.</p>

      <div className="flex gap-1 mb-4 border-b border-ink/10" data-testid="coach-feedback-workbench-tabs">
        {TABS.map((t) => (
          <button
            key={t.key} onClick={() => setTab(t.key)} data-testid={`coach-feedback-workbench-tab-${t.key}`}
            className={`px-3 py-2 text-sm font-medium flex items-center gap-1.5 border-b-2 -mb-px transition-colors ${tab === t.key ? "border-terracotta text-terracotta" : "border-transparent text-ink-soft hover:text-ink"}`}
          >
            <t.icon size={14} /> {t.label}
            {data && <span className="text-xs bg-ink/10 rounded-full px-1.5">{(data[t.key] || []).length}</span>}
          </button>
        ))}
      </div>

      {isPending ? (
        <div className="flex items-center gap-2 text-ink-soft" data-testid="coach-feedback-workbench-loading"><Loader2 size={16} className="animate-spin" /> Loading…</div>
      ) : isError ? (
        <p className="text-sm text-red-600" data-testid="coach-feedback-workbench-error">{error?.status === 403 ? "Coach access required." : "Couldn't load Feedback."}</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-ink-soft" data-testid="coach-feedback-workbench-empty">{TABS.find((t) => t.key === tab)?.empty}</p>
      ) : (
        <div className="space-y-2" data-testid="coach-feedback-workbench-list">
          {rows.map((row) => (
            <button
              key={row.item.frq_id || row.item.fbk_id}
              onClick={() => openRow(row)}
              className="w-full text-left border border-ink/10 bg-white/50 hover:border-terracotta/40 rounded-lg p-3.5 transition-colors"
              data-testid={`coach-feedback-workbench-row-${row.item.frq_id || row.item.fbk_id}`}
            >
              <div className="flex items-center justify-between">
                <p className="font-medium text-ink text-sm">{row.member_name || "Team"} — {row.workspace_title}</p>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] uppercase tracking-wide text-ink-soft bg-ink/5 rounded px-1.5 py-0.5">{row.access_type}</span>
                  <span className="text-[10px] uppercase tracking-wide text-terracotta bg-terracotta/10 rounded px-1.5 py-0.5">{SCOPE_LABEL[row.scope] || row.scope}</span>
                </div>
              </div>
              {row.kind === "request" ? (
                row.item.evidence?.label && <p className="text-xs text-ink-soft mt-1">Re: {row.item.evidence.label}</p>
              ) : (
                row.item.evidence?.[0]?.label && <p className="text-xs text-ink-soft mt-1">Re: {row.item.evidence[0].label}</p>
              )}
              {row.kind === "request" ? (
                <p className="text-xs text-ink-soft mt-1 truncate">{row.item.question_text}</p>
              ) : (
                row.item.focus && <p className="text-xs text-ink-soft mt-1 truncate">{row.item.focus}</p>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
